import type { ReactNode } from 'react';
import { token } from '@terra/ui-styled-system';
import {
  Badge,
  MetaDefault,
  MetricItem,
  Tooltip,
} from '@terra/ui-design-system';
import type { TerraIcon } from '@terra/ui-icons';
import { ListPageCellFrame } from '../../common/ListPageCellFrame';
import type { EnetAssetListCellValues } from './asset-list-cell-values';
import { ENET_ASSET_LIST_COLUMN } from './asset-list-columns';
import { listCountOrEmpty } from './format-asset-list-display';

const SCOPE_BADGE_BG: Record<string, string> = {
  IN_SCOPE: token('colors.bg.neutral.success'),
  OUT_OF_SCOPE: token('colors.bg.neutral.error'),
  PENDING: token('colors.bg.neutral.warning'),
};

export const emptyListCell = (
  <MetaDefault color="fg.muted" minW="48px" textAlign="center">
    -
  </MetaDefault>
);

export function TextOrDashCell({
  value,
}: {
  value?: string | null;
}): ReactNode {
  if (!value) {
    return emptyListCell;
  }

  return (
    <Tooltip title={value}>
      <MetaDefault color="fg.muted" truncate textAlign="center" maxW="100%">
        {value}
      </MetaDefault>
    </Tooltip>
  );
}

export function CountCell({
  count,
  icon: Icon,
  label,
  onSelect,
}: {
  count?: number;
  icon?: TerraIcon;
  label: string;
  onSelect?: () => void;
}): ReactNode {
  const resolvedCount = listCountOrEmpty({ count });

  if (resolvedCount === undefined) {
    return emptyListCell;
  }

  if (!Icon) {
    return (
      <MetaDefault color="fg.muted" textAlign="center">
        {resolvedCount}
      </MetaDefault>
    );
  }

  return (
    <MetricItem
      hideTooltip
      icon={<Icon size={16} />}
      label={label}
      metrics={resolvedCount}
      onClick={onSelect}
    />
  );
}

export function ScopeCell({
  scope,
  scopeLabel,
  scopeInherited,
  inheritedLabel,
}: {
  scope?: string | null;
  scopeLabel?: string;
  scopeInherited?: boolean;
  inheritedLabel: string;
}): ReactNode {
  if (!scope || scope === 'INHERIT' || !scopeLabel) {
    return emptyListCell;
  }

  const badge = (
    <Badge
      variant="fill"
      size="sm"
      bg={SCOPE_BADGE_BG[scope] ?? token('colors.bg.neutral.muted')}
    >
      {scopeLabel}
    </Badge>
  );

  if (!scopeInherited) {
    return badge;
  }

  return <Tooltip title={inheritedLabel}>{badge}</Tooltip>;
}

export function TypeBadgeCell({
  typeLabel,
  typeColorPalette,
}: {
  typeLabel?: string;
  typeColorPalette?: string;
}): ReactNode {
  if (!typeLabel || !typeColorPalette) {
    return emptyListCell;
  }

  return (
    <Badge variant="outline" size="sm" colorPalette={typeColorPalette}>
      {typeLabel}
    </Badge>
  );
}

export function TlsCell({
  tlsEnabled,
  tlsLabel,
}: {
  tlsEnabled?: boolean;
  tlsLabel?: string;
}): ReactNode {
  if (!tlsEnabled || !tlsLabel) {
    return emptyListCell;
  }

  return (
    <Badge variant="outline" size="sm" colorPalette="green">
      {tlsLabel}
    </Badge>
  );
}

export function renderAssetListValueCell({
  column,
  values,
  countIcons,
  countLabels,
  inheritedLabel,
  onCountSelect,
}: {
  column: string;
  values: EnetAssetListCellValues;
  countIcons: Partial<
    Record<'hosts' | 'subdomains' | 'domains' | 'services', TerraIcon>
  >;
  countLabels: Partial<
    Record<'hosts' | 'subdomains' | 'domains' | 'services', string>
  >;
  inheritedLabel: string;
  onCountSelect?: () => void;
}): ReactNode {
  switch (column) {
    case ENET_ASSET_LIST_COLUMN.assetType:
      return (
        <TypeBadgeCell
          typeLabel={values.typeLabel}
          typeColorPalette={values.typeColorPalette}
        />
      );
    case ENET_ASSET_LIST_COLUMN.attribution:
      return <TextOrDashCell value={values.attribution} />;
    case ENET_ASSET_LIST_COLUMN.scope:
      return (
        <ScopeCell
          scope={values.scope}
          scopeLabel={values.scopeLabel}
          scopeInherited={values.scopeInherited}
          inheritedLabel={inheritedLabel}
        />
      );
    case ENET_ASSET_LIST_COLUMN.source:
      return <TextOrDashCell value={values.source} />;
    case ENET_ASSET_LIST_COLUMN.ip:
      return <TextOrDashCell value={values.ip} />;
    case ENET_ASSET_LIST_COLUMN.registrant:
      return <TextOrDashCell value={values.registrant} />;
    case ENET_ASSET_LIST_COLUMN.country:
      return <TextOrDashCell value={values.country} />;
    case ENET_ASSET_LIST_COLUMN.hosts:
      return (
        <CountCell
          count={values.hostsCount}
          icon={countIcons.hosts}
          label={countLabels.hosts ?? ''}
          onSelect={onCountSelect}
        />
      );
    case ENET_ASSET_LIST_COLUMN.level:
      return <TextOrDashCell value={values.level} />;
    case ENET_ASSET_LIST_COLUMN.subdomains:
      return (
        <CountCell
          count={values.subdomainsCount}
          icon={countIcons.subdomains}
          label={countLabels.subdomains ?? ''}
          onSelect={onCountSelect}
        />
      );
    case ENET_ASSET_LIST_COLUMN.dnsRecords:
      return <CountCell count={values.dnsRecordsCount} label="" />;
    case ENET_ASSET_LIST_COLUMN.resolvesTo:
      return <TextOrDashCell value={values.resolvesTo} />;
    case ENET_ASSET_LIST_COLUMN.registrar:
      return <TextOrDashCell value={values.registrar} />;
    case ENET_ASSET_LIST_COLUMN.reverseDns:
      return <TextOrDashCell value={values.reverseDns} />;
    case ENET_ASSET_LIST_COLUMN.netblock:
      return <TextOrDashCell value={values.netblock} />;
    case ENET_ASSET_LIST_COLUMN.provider:
      return <TextOrDashCell value={values.provider} />;
    case ENET_ASSET_LIST_COLUMN.function:
      return <TextOrDashCell value={values.functionLabel} />;
    case ENET_ASSET_LIST_COLUMN.domains:
      return (
        <CountCell
          count={values.domainsCount}
          icon={countIcons.domains}
          label={countLabels.domains ?? ''}
          onSelect={onCountSelect}
        />
      );
    case ENET_ASSET_LIST_COLUMN.services:
      return (
        <CountCell
          count={values.servicesCount}
          icon={countIcons.services}
          label={countLabels.services ?? ''}
          onSelect={onCountSelect}
        />
      );
    case ENET_ASSET_LIST_COLUMN.transport:
      return <TextOrDashCell value={values.transport} />;
    case ENET_ASSET_LIST_COLUMN.host:
      return <TextOrDashCell value={values.host} />;
    case ENET_ASSET_LIST_COLUMN.tls:
      return (
        <TlsCell tlsEnabled={values.tlsEnabled} tlsLabel={values.tlsLabel} />
      );
    case ENET_ASSET_LIST_COLUMN.vendor:
      return <TextOrDashCell value={values.vendor} />;
    case ENET_ASSET_LIST_COLUMN.domain:
      return <TextOrDashCell value={values.domain} />;
    case ENET_ASSET_LIST_COLUMN.category:
      return <TextOrDashCell value={values.category} />;
    case ENET_ASSET_LIST_COLUMN.detection:
      return <TextOrDashCell value={values.detection} />;
    case ENET_ASSET_LIST_COLUMN.access:
      return <TextOrDashCell value={values.access} />;
    default:
      return emptyListCell;
  }
}

export function AssetListCellSlot({
  children,
}: {
  children: ReactNode;
}): ReactNode {
  return <ListPageCellFrame justify="center">{children}</ListPageCellFrame>;
}
